import { scaleBand, scaleLinear, scalePoint } from "d3-scale";
import { arc as d3arc, area as d3area, line as d3line, curveMonotoneX, curveLinear, pie as d3pie } from "d3-shape";
import type { VNode, Theme } from "@broadpaper/core";
import { h } from "@broadpaper/core";

export type ChartType = "bar" | "horizontalBar" | "line" | "area" | "pie" | "donut";

export interface ChartSeriesData {
  label: string;
  values: Array<number | null>;
  color: string;
}

export interface ChartData {
  categories: string[];
  series: ChartSeriesData[];
}

/**
 * One thing a reader can point at: a bar, a slice, a marker on a line.
 *
 * The chart records these as it draws, because it is the only code that knows
 * which mark is which datum. Nothing is drawn for them and no geometry is
 * duplicated — the mark itself carries `data-bp-hit="<index>"`, so a screen
 * renderer can find the element and use its real shape, and a PDF renderer can
 * drop the attribute and produce exactly the vectors it produced before.
 */
export interface ChartHit {
  /** Index into the chart's category list. */
  categoryIndex: number;
  category: string;
  seriesIndex: number;
  series: string;
  value: number | null;
  /** The value as the report itself formats it. */
  formatted: string;
  color: string;
  /** For pie and donut: this slice's share of the whole. */
  share?: number;
}

export interface ChartRenderOptions {
  type: ChartType;
  width: number;
  height: number;
  theme: Theme;
  fontFamily: string;
  showLegend: boolean;
  showValues: boolean;
  showGrid: boolean;
  stacked: boolean;
  smooth: boolean;
  /** Formats axis ticks and value labels. */
  formatValue: (n: number) => string;
  /** Formats pie/donut percentages. */
  formatPercent: (fraction: number) => string;
  innerLabel?: string;
  /**
   * Filled in as the chart draws, in the same order as the `data-bp-hit`
   * indices on the marks. Left out entirely when nobody is going to read it.
   */
  hits?: ChartHit[];
}

const FONT_SIZE = 9;
const AXIS_COLOR = "#9AA1AB";

/**
 * Records a hit and returns the attributes that tie the mark to it.
 *
 * Returns nothing at all when no one asked for hits, so a chart rendered for
 * print is byte-for-byte the chart it was before this existed.
 */
function hit(opts: ChartRenderOptions, entry: ChartHit): Record<string, string> {
  if (!opts.hits) return {};
  opts.hits.push(entry);
  return { "data-bp-hit": String(opts.hits.length - 1) };
}

function fmt(n: number): string {
  return Math.round(n * 100) / 100 + "";
}

function legend(data: ChartData, opts: ChartRenderOptions, y: number, width: number): { node: VNode | null; height: number } {
  if (!opts.showLegend || data.series.length === 0) return { node: null, height: 0 };
  const items = opts.type === "pie" || opts.type === "donut" ? data.categories.map((c, i) => ({ label: c, color: colorAt(data, i) })) : data.series.map((s) => ({ label: s.label, color: s.color }));
  const perRow = Math.max(1, Math.floor(width / 120));
  const rows = Math.ceil(items.length / perRow);
  const node = h(
    "g",
    { class: "bp-chart__legend", transform: `translate(0, ${y})` },
    items.map((it, i) => {
      const col = i % perRow;
      const row = Math.floor(i / perRow);
      const x = (width / perRow) * col;
      return h("g", { transform: `translate(${fmt(x)}, ${row * 16})` }, h("rect", { x: 0, y: 2, width: 10, height: 10, rx: 2, fill: it.color }), h("text", { x: 15, y: 11, "font-size": FONT_SIZE + 1, "font-family": opts.fontFamily, fill: opts.theme.colors.text }, truncate(it.label, Math.floor(width / perRow / 6))));
    })
  );
  return { node, height: rows * 16 + 4 };
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, Math.max(1, n - 1)) + "…" : s;
}

export function colorAt(data: ChartData, i: number): string {
  const s = data.series[0];
  const palette = data.series.map((x) => x.color);
  return palette[i % Math.max(1, palette.length)] ?? s?.color ?? "#888";
}

/** Renders a chart to an SVG VNode. Pure and deterministic. */
export function renderChart(data: ChartData, opts: ChartRenderOptions): VNode {
  const { width, height } = opts;
  if (opts.type === "pie" || opts.type === "donut") return renderPie(data, opts);

  const horizontal = opts.type === "horizontalBar";
  const lg = legend(data, opts, 0, width);
  const marginTop = lg.height + 6;
  const marginRight = 8;
  const marginBottom = 24;
  const allValues = data.series.flatMap((s) => s.values.map((v) => v ?? 0));
  const stackedTotals = data.categories.map((_, i) => data.series.reduce((a, s) => a + Math.max(0, s.values[i] ?? 0), 0));
  const stackedNeg = data.categories.map((_, i) => data.series.reduce((a, s) => a + Math.min(0, s.values[i] ?? 0), 0));
  const maxV = Math.max(0, ...(opts.stacked ? stackedTotals : allValues));
  const minV = Math.min(0, ...(opts.stacked ? stackedNeg : allValues));
  const vScale = scaleLinear().domain([minV, maxV === minV ? maxV + 1 : maxV]).nice(5);
  const ticks = vScale.ticks(5);
  const tickLabels = ticks.map(opts.formatValue);
  const labelWidth = Math.max(...tickLabels.map((t) => t.length * FONT_SIZE * 0.6), 18);
  const marginLeft = horizontal ? Math.min(width * 0.35, Math.max(...data.categories.map((c) => c.length)) * FONT_SIZE * 0.6 + 8) : labelWidth + 8;
  const plotW = Math.max(10, width - marginLeft - marginRight);
  const plotH = Math.max(10, height - marginTop - marginBottom);

  const children: VNode[] = [];
  if (lg.node) children.push(lg.node);

  const g = (x: number, y: number, ...kids: Array<VNode | null>) => h("g", { transform: `translate(${fmt(x)}, ${fmt(y)})` }, kids);

  if (horizontal) {
    vScale.range([0, plotW]);
    const band = scaleBand<string>().domain(data.categories.map((_, i) => String(i))).range([0, plotH]).paddingInner(0.25).paddingOuter(0.1);
    const inner = scaleBand<string>().domain(data.series.map((_, i) => String(i))).range([0, band.bandwidth()]).padding(0.1);
    const grid = opts.showGrid ? ticks.map((t) => h("line", { x1: vScale(t), x2: vScale(t), y1: 0, y2: plotH, stroke: opts.theme.colors.border, "stroke-width": 1 })) : [];
    const axis = ticks.map((t) => h("text", { x: vScale(t), y: plotH + 14, "text-anchor": "middle", "font-size": FONT_SIZE, "font-family": opts.fontFamily, fill: AXIS_COLOR }, opts.formatValue(t)));
    const cats = data.categories.map((c, i) => h("text", { x: -6, y: (band(String(i)) ?? 0) + band.bandwidth() / 2 + 3, "text-anchor": "end", "font-size": FONT_SIZE, "font-family": opts.fontFamily, fill: opts.theme.colors.text }, truncate(c, Math.floor(marginLeft / (FONT_SIZE * 0.6)))));
    const bars: VNode[] = [];
    data.categories.forEach((_, ci) => {
      let posAcc = 0;
      let negAcc = 0;
      data.series.forEach((s, si) => {
        const v = s.values[ci] ?? 0;
        let x0: number, x1: number;
        if (opts.stacked) {
          if (v >= 0) {
            x0 = vScale(posAcc);
            x1 = vScale(posAcc + v);
            posAcc += v;
          } else {
            x0 = vScale(negAcc + v);
            x1 = vScale(negAcc);
            negAcc += v;
          }
        } else {
          x0 = vScale(Math.min(0, v));
          x1 = vScale(Math.max(0, v));
        }
        const y = (band(String(ci)) ?? 0) + (opts.stacked ? 0 : inner(String(si)) ?? 0);
        const bh = opts.stacked ? band.bandwidth() : inner.bandwidth();
        bars.push(h("rect", { x: fmt(x0), y: fmt(y), width: fmt(Math.max(0, x1 - x0)), height: fmt(bh), fill: s.color, rx: 1.5, ...hit(opts, { categoryIndex: ci, category: data.categories[ci] ?? "", seriesIndex: si, series: s.label, value: s.values[ci] ?? null, formatted: opts.formatValue(v), color: s.color }) }));
        if (opts.showValues && !opts.stacked) bars.push(h("text", { x: fmt(x1 + 4), y: fmt(y + bh / 2 + 3), "font-size": FONT_SIZE, "font-family": opts.fontFamily, fill: opts.theme.colors.text }, opts.formatValue(v)));
      });
    });
    children.push(g(marginLeft, marginTop, ...grid, h("line", { x1: vScale(0), x2: vScale(0), y1: 0, y2: plotH, stroke: AXIS_COLOR }), ...bars, ...axis, ...cats));
    return svg(width, height, children);
  }

  vScale.range([plotH, 0]);
  const grid = opts.showGrid ? ticks.map((t) => h("line", { x1: 0, x2: plotW, y1: fmt(vScale(t)), y2: fmt(vScale(t)), stroke: opts.theme.colors.border, "stroke-width": 1 })) : [];
  const yAxis = ticks.map((t) => h("text", { x: -6, y: fmt(vScale(t) + 3), "text-anchor": "end", "font-size": FONT_SIZE, "font-family": opts.fontFamily, fill: AXIS_COLOR }, opts.formatValue(t)));
  const maxCatLabels = Math.max(1, Math.floor(plotW / 48));
  const every = Math.ceil(data.categories.length / maxCatLabels);

  if (opts.type === "bar") {
    const band = scaleBand<string>().domain(data.categories.map((_, i) => String(i))).range([0, plotW]).paddingInner(0.25).paddingOuter(0.1);
    const inner = scaleBand<string>().domain(data.series.map((_, i) => String(i))).range([0, band.bandwidth()]).padding(0.1);
    const bars: VNode[] = [];
    data.categories.forEach((_, ci) => {
      let posAcc = 0;
      let negAcc = 0;
      data.series.forEach((s, si) => {
        const v = s.values[ci] ?? 0;
        let y0: number, y1: number;
        if (opts.stacked) {
          if (v >= 0) {
            y1 = vScale(posAcc);
            y0 = vScale(posAcc + v);
            posAcc += v;
          } else {
            y1 = vScale(negAcc + v);
            y0 = vScale(negAcc);
            negAcc += v;
          }
        } else {
          y0 = vScale(Math.max(0, v));
          y1 = vScale(Math.min(0, v));
        }
        const x = (band(String(ci)) ?? 0) + (opts.stacked ? 0 : inner(String(si)) ?? 0);
        const bw = opts.stacked ? band.bandwidth() : inner.bandwidth();
        bars.push(h("rect", { x: fmt(x), y: fmt(y0), width: fmt(bw), height: fmt(Math.max(0, y1 - y0)), fill: s.color, rx: 1.5, ...hit(opts, { categoryIndex: ci, category: data.categories[ci] ?? "", seriesIndex: si, series: s.label, value: s.values[ci] ?? null, formatted: opts.formatValue(v), color: s.color }) }));
        if (opts.showValues && !opts.stacked) bars.push(h("text", { x: fmt(x + bw / 2), y: fmt(y0 - 3), "text-anchor": "middle", "font-size": FONT_SIZE - 1, "font-family": opts.fontFamily, fill: opts.theme.colors.text }, opts.formatValue(v)));
      });
    });
    const xAxis = data.categories.map((c, i) => (i % every === 0 ? h("text", { x: fmt((band(String(i)) ?? 0) + band.bandwidth() / 2), y: plotH + 14, "text-anchor": "middle", "font-size": FONT_SIZE, "font-family": opts.fontFamily, fill: AXIS_COLOR }, truncate(c, 10)) : null));
    children.push(g(marginLeft, marginTop, ...grid, ...bars, h("line", { x1: 0, x2: plotW, y1: fmt(vScale(0)), y2: fmt(vScale(0)), stroke: AXIS_COLOR }), ...yAxis, ...xAxis));
    return svg(width, height, children);
  }

  // line / area
  const xScale = scalePoint<string>().domain(data.categories.map((_, i) => String(i))).range([0, plotW]).padding(0.15);
  const curve = opts.smooth ? curveMonotoneX : curveLinear;
  const shapes: VNode[] = [];
  const stackBase: number[] = data.categories.map(() => 0);
  data.series.forEach((s, si) => {
    const pts = s.values.map((v, i) => ({ i, v: v ?? 0 }));
    const base = [...stackBase];
    if (opts.stacked) pts.forEach((p) => (stackBase[p.i] = (stackBase[p.i] ?? 0) + p.v));
    const yOf = (p: { i: number; v: number }) => vScale(opts.stacked ? (base[p.i] ?? 0) + p.v : p.v);
    const y0Of = (p: { i: number }) => vScale(opts.stacked ? base[p.i] ?? 0 : 0);
    const l = d3line<{ i: number; v: number }>().x((p) => xScale(String(p.i)) ?? 0).y(yOf).curve(curve);
    if (opts.type === "area") {
      const a = d3area<{ i: number; v: number }>().x((p) => xScale(String(p.i)) ?? 0).y0(y0Of).y1(yOf).curve(curve);
      shapes.push(h("path", { d: a(pts) ?? "", fill: s.color, "fill-opacity": 0.18 }));
    }
    shapes.push(h("path", { d: l(pts) ?? "", fill: "none", stroke: s.color, "stroke-width": 2, "stroke-linejoin": "round", "stroke-linecap": "round" }));
    // A marker is drawn only when the points are far enough apart to see, but
    // every point is still something a reader can ask about, so the hit is
    // recorded either way.
    pts.forEach((p) => {
      const attrs = hit(opts, { categoryIndex: p.i, category: data.categories[p.i] ?? "", seriesIndex: si, series: s.label, value: s.values[p.i] ?? null, formatted: opts.formatValue(p.v), color: s.color });
      if (pts.length <= 40) shapes.push(h("circle", { cx: fmt(xScale(String(p.i)) ?? 0), cy: fmt(yOf(p)), r: 2.5, fill: "#fff", stroke: s.color, "stroke-width": 1.5, ...attrs }));
      else if (opts.hits) opts.hits[opts.hits.length - 1]!.value = s.values[p.i] ?? null;
    });
    if (opts.showValues) pts.forEach((p) => shapes.push(h("text", { x: fmt(xScale(String(p.i)) ?? 0), y: fmt(yOf(p) - 6), "text-anchor": "middle", "font-size": FONT_SIZE - 1, "font-family": opts.fontFamily, fill: opts.theme.colors.text }, opts.formatValue(p.v))));
  });
  const xAxis = data.categories.map((c, i) => (i % every === 0 ? h("text", { x: fmt(xScale(String(i)) ?? 0), y: plotH + 14, "text-anchor": "middle", "font-size": FONT_SIZE, "font-family": opts.fontFamily, fill: AXIS_COLOR }, truncate(c, 10)) : null));
  children.push(g(marginLeft, marginTop, ...grid, h("line", { x1: 0, x2: plotW, y1: fmt(vScale(0)), y2: fmt(vScale(0)), stroke: AXIS_COLOR }), ...shapes, ...yAxis, ...xAxis));
  return svg(width, height, children);
}

function renderPie(data: ChartData, opts: ChartRenderOptions): VNode {
  const { width, height } = opts;
  const series = data.series[0];
  const values = data.categories.map((_, i) => Math.max(0, series?.values[i] ?? 0));
  const total = values.reduce((a, b) => a + b, 0);
  const legendW = opts.showLegend ? Math.min(160, width * 0.45) : 0;
  const size = Math.min(height, width - legendW);
  const r = Math.max(10, size / 2 - 6);
  const cx = (width - legendW) / 2;
  const cy = height / 2;
  const inner = opts.type === "donut" ? r * 0.62 : 0;
  const pie = d3pie<number>().sort(null).value((d) => d);
  const arcs = pie(values);
  const arcGen = d3arc<{ startAngle: number; endAngle: number }>().innerRadius(inner).outerRadius(r).padAngle(0.01).cornerRadius(2);
  const labelArc = d3arc<{ startAngle: number; endAngle: number }>().innerRadius((inner + r) / 2).outerRadius((inner + r) / 2);
  const slices = arcs.map((a, i) => {
    const frac = total > 0 ? values[i]! / total : 0;
    const [lx, ly] = labelArc.centroid(a);
    const attrs = hit(opts, {
      categoryIndex: i,
      category: data.categories[i] ?? "",
      seriesIndex: 0,
      series: series?.label ?? "",
      value: series?.values[i] ?? null,
      formatted: opts.formatValue(values[i] ?? 0),
      color: colorAt(data, i),
      share: frac
    });
    return h("g", null, h("path", { d: arcGen(a) ?? "", fill: colorAt(data, i), ...attrs }), opts.showValues && frac >= 0.06 ? h("text", { x: fmt(lx), y: fmt(ly + 3), "text-anchor": "middle", "font-size": FONT_SIZE, "font-family": opts.fontFamily, fill: "#fff", "font-weight": "600" }, opts.formatPercent(frac)) : null);
  });
  const children: VNode[] = [h("g", { transform: `translate(${fmt(cx)}, ${fmt(cy)})` }, total === 0 ? h("circle", { r, fill: opts.theme.colors.border }) : slices, opts.type === "donut" && opts.innerLabel ? h("text", { "text-anchor": "middle", y: 4, "font-size": FONT_SIZE + 3, "font-weight": "700", "font-family": opts.fontFamily, fill: opts.theme.colors.text }, opts.innerLabel) : null)];
  if (opts.showLegend) {
    const items = data.categories.map((c, i) => ({ label: c, color: colorAt(data, i), frac: total > 0 ? values[i]! / total : 0 }));
    const lineH = 15;
    const startY = Math.max(0, cy - (items.length * lineH) / 2);
    children.push(
      h(
        "g",
        { transform: `translate(${fmt(width - legendW)}, ${fmt(startY)})` },
        items.map((it, i) => h("g", { transform: `translate(0, ${i * lineH})` }, h("rect", { x: 0, y: 2, width: 9, height: 9, rx: 2, fill: it.color }), h("text", { x: 14, y: 10, "font-size": FONT_SIZE, "font-family": opts.fontFamily, fill: opts.theme.colors.text }, `${truncate(it.label, Math.floor(legendW / 6) - 6)} ${opts.formatPercent(it.frac)}`)))
      )
    );
  }
  return svg(width, height, children);
}

function svg(width: number, height: number, children: VNode[]): VNode {
  return h("svg", { xmlns: "http://www.w3.org/2000/svg", width: fmt(width), height: fmt(height), viewBox: `0 0 ${fmt(width)} ${fmt(height)}`, class: "bp-chart__svg", role: "img", style: { display: "block", overflow: "visible" } }, children);
}
